import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { projectService, activityService } from '../../../utils/supabaseQueries';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const NewProjectModal = ({ isOpen, onClose, onProjectCreated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'film', // Default to Film as requested
    template: 'clean', // Default to Clean as requested  
    budget: 'under_500k' // Default to Under 500k as requested
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Updated project types as requested
  const projectTypes = [
    { value: 'film', label: 'Film' },
    { value: 'short_film', label: 'Short Film' },
    { value: 'tv', label: 'TV' },
    { value: 'youtube', label: 'Youtube' },
    { value: 'vertical', label: 'Vertical' }
  ];

  // Updated template options as requested
  const templateOptions = [
    { value: 'clean', label: 'Clean' },
    { value: 'colorful', label: 'Colorful' },
    { value: 'rapid', label: 'Rapid' }
  ];

  // Updated budget options as requested
  const budgetOptions = [
    { value: 'under_500k', label: 'Under 500k' },
    { value: 'under_5m', label: 'Under 5M' },
    { value: 'above_5m', label: 'Above 5M' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.name?.trim()) {
      setError('Project name is required');
      return;
    }

    if (!user) {
      setError('You must be logged in to create a project');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create project with updated metadata structure
      const projectData = {
        name: formData?.name?.trim(),
        description: formData?.description?.trim() || `New ${formData?.type?.replace('_', ' ')} project`,
        status: 'draft',
        metadata: {
          type: formData?.type,
          template: formData?.template,
          budget: formData?.budget,
          createdFrom: 'project_management'
        }
      };

      const { data: newProject, error: createError } = await projectService?.createProject(projectData);
      
      if (createError) throw createError;

      // Log activity
      if (newProject?.id) {
        await activityService?.logActivity(newProject?.id, 'project_created', {
          projectType: formData?.type,
          template: formData?.template,
          budget: formData?.budget
        });
      }

      // Reset form to defaults
      setFormData({
        name: '',
        description: '',
        type: 'film',
        template: 'clean',
        budget: 'under_500k'
      });

      // Notify parent component
      onProjectCreated?.(newProject);
      
      // Close modal
      onClose();

    } catch (error) {
      console.error('Error creating project:', error);
      setError(error?.message || 'Failed to create project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        description: '',
        type: 'film',
        template: 'clean',
        budget: 'under_500k'
      });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Plus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Create New Project</h2>
              <p className="text-sm text-muted-foreground">Start a new film credits project</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
            className="h-8 w-8 p-0"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Project Name *
            </label>
            <Input
              type="text"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              placeholder="Enter project name..."
              disabled={isSubmitting}
              className="w-full"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              placeholder="Brief description of your project..."
              disabled={isSubmitting}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Project Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Type
            </label>
            <Select
              value={formData?.type}
              onChange={(value) => handleInputChange('type', value)}
              options={projectTypes}
              disabled={isSubmitting}
            />
          </div>

          {/* Template */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Template
            </label>
            <Select
              value={formData?.template}
              onChange={(value) => handleInputChange('template', value)}
              options={templateOptions}
              disabled={isSubmitting}
            />
          </div>

          {/* Budget */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Budget
            </label>
            <Select
              value={formData?.budget}
              onChange={(value) => handleInputChange('budget', value)}
              options={budgetOptions}
              disabled={isSubmitting}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData?.name?.trim()}
              fullWidth
              iconName={isSubmitting ? "Loader2" : "Plus"}
              className={isSubmitting ? "animate-spin" : ""}
            >
              {isSubmitting ? 'Creating...' : 'Create Project'}
            </Button>
          </div>
        </form>

        {/* Info Footer */}
        <div className="px-6 pb-6">
          <div className="bg-muted/50 rounded-md p-3">
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tip:</strong> After creating your project, you'll be able to add credits manually or import them from an Excel file.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewProjectModal;